import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, Sparkles, CheckCircle2, AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/app/PasswordInput";
import LanguageToggle from "@/components/app/LanguageToggle";
import { api, detailText } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }
    const checkToken = async () => {
      setVerifying(true);
      try {
        const res = await api.get(`/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        setTokenValid(res.data?.valid || false);
        setUserInfo(res.data);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    checkToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password minimal 6 karakter");
    if (password !== confirmPassword) return toast.error("Konfirmasi password tidak cocok");

    setLoading(true);
    try {
      const res = await api.post("/auth/reset-password-token", {
        token,
        new_password: password,
      });
      toast.success(res.data?.message || "Password berhasil diubah!");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal mengubah password");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen w-full grid place-items-center bg-[#FFFCF9]">
        <div className="flex flex-col items-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#BE5366]" />
          <p className="text-xs font-semibold text-[#90777D]">Memverifikasi link reset password…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-[#FFFCF9] text-[#35282C] relative selection:bg-[#F9EFF0] selection:text-[#9E4053]">
      {/* Top Left Navigation Back to Landing */}
      <div className="absolute top-4 left-4 z-30 md:top-6 md:left-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#90777D] hover:text-[#BE5366] bg-white/80 backdrop-blur-md px-3.5 py-2 rounded-full border border-[#D3B68C]/30 shadow-sm transition-all"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Kembali ke Login</span>
        </Link>
      </div>

      {/* Top Right Language Switcher */}
      <div className="absolute top-4 right-4 z-30 md:top-6 md:right-6">
        <LanguageToggle />
      </div>

      {/* Left Editorial Visual Banner */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=80"
          alt="Weddly Editorial Wedding"
          className="absolute inset-0 h-full w-full object-cover filter brightness-[0.7] contrast-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#35282C]/90 via-[#35282C]/50 to-[#9E4053]/30" />

        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <span className="font-serif-title text-2xl font-bold tracking-tight">Weddly</span>
          </div>

          <div className="space-y-4 max-w-md">
            <h2 className="font-serif-title text-3xl font-bold leading-tight">
              Akses Aman &amp; Mudah untuk Pernikahanmu
            </h2>
            <p className="text-xs text-[#F1E5E7] leading-relaxed">
              Buat password baru secara langsung untuk melanjutkan perencanaan momen istimewa berdua.
            </p>
          </div>

          <div className="text-[11px] text-[#D3B68C]">
            &copy; {new Date().getFullYear()} Weddly Indonesia. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 md:px-16 lg:px-20">
        <div className="mx-auto w-full max-w-sm space-y-6">
          {!tokenValid ? (
            <Card className="border-rose-200 bg-white shadow-sm p-6 text-center space-y-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-bold text-[#35282C]">Link Reset Tidak Valid</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Link reset password ini tidak valid, sudah digunakan, atau telah kadaluarsa.
                </p>
              </div>
              <Button
                onClick={() => navigate("/login")}
                className="w-full rounded-full bg-[#BE5366] hover:bg-[#9E4053] text-white text-xs h-10 font-semibold shadow-md"
              >
                Kembali ke Halaman Login
              </Button>
            </Card>
          ) : (
            <>
              <div className="space-y-1 text-center sm:text-left">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F9EFF0] px-3 py-1 text-xs font-semibold text-[#BE5366]">
                  <KeyRound className="h-3.5 w-3.5" /> Reset Password
                </div>
                <h1 className="font-serif-title text-2xl sm:text-3xl font-bold text-[#35282C] pt-2">
                  Buat Password Baru
                </h1>
                <p className="text-xs text-[#90777D]">
                  Halo, <strong>{userInfo?.name}</strong>. Silakan masukkan password baru untuk akun Weddly Anda.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                <div>
                  <Label className="text-xs font-bold text-[#35282C]">Password Baru</Label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimal 6 karakter"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold text-[#35282C]">Konfirmasi Password Baru</Label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ulangi password baru"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-full text-sm font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-md transition-all mt-4"
                >
                  {loading ? "Menyimpan Password..." : "Simpan Password Baru"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
